import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, Monitor, ShieldCheck, Pin, PinOff } from 'lucide-react';
import { Participant } from '../types';

interface VideoTileProps {
  participant: Participant;
  isLocal?: boolean;
  stream?: MediaStream | null;
  className?: string;
  isPinned?: boolean;
  onTogglePin?: () => void;
}

export const VideoTile: React.FC<VideoTileProps> = ({
  participant,
  isLocal = false,
  stream,
  className = '',
  isPinned = false,
  onTogglePin,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    if (!isLocal && audioRef.current && stream) {
      audioRef.current.srcObject = stream;
      audioRef.current.play().catch(() => {});
    }
  }, [stream, isLocal]);

  const initials = participant.name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isSpeaking = participant.speaking;
  const isVideoActive =
    (participant.video || participant.screen) &&
    (stream ? stream.getVideoTracks().some((t) => t.enabled) : true);

  return (
    <div
      className={`relative group rounded-2xl overflow-hidden bg-[#121622] border transition-all duration-300 flex items-center justify-center ${
        isPinned
          ? 'border-blue-500 ring-2 ring-blue-500/50 shadow-[0_0_24px_rgba(59,130,246,0.3)]'
          : isSpeaking
          ? 'border-emerald-500 ring-2 ring-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.25)]'
          : 'border-slate-800/80 hover:border-slate-700'
      } ${className}`}
    >
      {/* Remote Audio Track Player (Guarantees voice audio is heard even when camera is off) */}
      {!isLocal && (
        <audio
          ref={audioRef}
          autoPlay
          playsInline
        />
      )}

      {/* Video Element (Supports both Camera & Screen Share) */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={`w-full h-full ${
          participant.screen ? 'object-contain bg-black' : 'object-cover'
        } ${isLocal && !participant.screen ? 'scale-x-[-1]' : ''} ${
          !isVideoActive ? 'hidden' : ''
        }`}
      />

      {/* Camera Off Avatar Placeholder */}
      {!isVideoActive && (
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-blue-500/10">
            {initials || 'U'}
          </div>
          <span className="text-slate-400 text-sm font-medium">{participant.name}</span>
        </div>
      )}

      {/* Screen Sharing Badge */}
      {participant.screen && (
        <div className="absolute top-3 left-3 bg-blue-600/90 backdrop-blur px-2.5 py-1 rounded-lg flex items-center space-x-1.5 text-xs text-white font-medium shadow-md">
          <Monitor className="w-3.5 h-3.5" />
          <span>Screen</span>
        </div>
      )}

      {/* Hand Raised Badge */}
      {participant.handRaised && (
        <div className="absolute top-3 right-12 bg-amber-500/90 backdrop-blur px-2.5 py-1 rounded-lg flex items-center space-x-1 text-xs text-white font-bold shadow-md animate-bounce">
          <span>✋</span>
          <span>Raised Hand</span>
        </div>
      )}

      {/* Pin / Unpin Action Button & Indicator */}
      {onTogglePin && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          title={isPinned ? 'Unpin participant' : 'Pin participant to stage'}
          className={`absolute top-3 right-3 p-2 rounded-xl backdrop-blur-md border transition-all z-10 shadow-lg ${
            isPinned
              ? 'bg-blue-600 text-white border-blue-400 opacity-100'
              : 'bg-slate-900/80 text-slate-300 hover:text-white hover:bg-slate-800 border-slate-700/70 opacity-0 group-hover:opacity-100'
          }`}
        >
          {isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
        </button>
      )}

      {/* Bottom info bar */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
        <div className="bg-[#090b10]/75 backdrop-blur-md px-3 py-1.5 rounded-lg flex items-center space-x-2 border border-slate-700/40 text-xs">
          <span className="text-white font-medium truncate max-w-[140px]">
            {participant.name} {isLocal && '(You)'}
          </span>
          {participant.role === 'host' && (
            <span className="flex items-center space-x-0.5 text-blue-400 font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Host</span>
            </span>
          )}
        </div>

        {/* Audio state indicator */}
        <div
          className={`p-1.5 rounded-lg backdrop-blur-md border ${
            participant.audio
              ? 'bg-slate-800/80 border-slate-700/40 text-emerald-400'
              : 'bg-rose-500/20 border-rose-500/40 text-rose-400'
          }`}
        >
          {participant.audio ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
        </div>
      </div>
    </div>
  );
};
